/* eslint-disable no-case-declarations */
import {
  type AnyElement,
  type VNode,
  parseNumber,
  toCamel,
  toKebab,
  DomMatrix
} from "@zyzgroup/core-common";
import { getVNode, scheduleVNodeAsap, updateVNode } from "../components/";
import { domPrefixes } from "./dom";
import { ajax } from "../ajax";

// "transitionend"
// "webkitTransitionEnd"
// "mozTransitionEnd"
// "oTransitionEnd"
// "msTransitionEnd"
// "MSTransitionEnd" ?
function getPrefixedStyleNames(styleName: string): string[] {
  const names = [];
  for (let i = 0, l = domPrefixes.length; i < l; i++) {
    const name = toKebab(domPrefixes[i] + styleName);
    names.push(name);
  }
  return names;
}

////// window.getComputedStyle() 可能导致性能问题的一些因素：
// 1. 计算成本：
// getComputedStyle 涉及到遍历元素的所有 CSS 规则（包括层叠和继承），计算出每个属性的最终值
// 对于复杂的 CSS 树结构、大量样式规则或频繁变动的动态样式，这种计算可能较为耗时，尤其是在大型或复杂的网页中
// 2. DOM 访问：
// 调用 getComputedStyle 通常伴随着 DOM 元素的访问。频繁地跨 DOM 查询元素，特别是深度嵌套或数量众多的元素，会增加 DOM 访问的成本，导致潜在的性能瓶颈
// 3. 重排与重绘：
// 如果 getComputedStyle 被触发在元素样式改变之后，且这些变化影响了布局（如尺寸、位置变化），浏览器可能需要进行重新布局（重排）和/或重新绘制
// 这些操作通常是昂贵的，尤其是在没有优化的情况下连续触发
// 4. 内存消耗：
// getComputedStyle 返回一个 CSSStyleDeclaration 对象，它包含了元素所有计算后的样式属性
// 频繁调用并保存这些对象可能会增加内存消耗，特别是在循环或定时器中持续查询多个元素时
// 5. 阻塞主线程：
// 如果在关键渲染路径（如页面加载或用户交互期间）中同步调用 getComputedStyle，可能会阻塞浏览器主线程，延迟其他重要任务（如脚本执行、布局、绘制等）的处理，从而影响页面响应速度和用户体验
////// 为了缓解上述潜在的性能问题，可以采取以下策略：
// 1. 缓存计算结果：如果可能，尽量避免对同一元素的样式进行重复查询。将 getComputedStyle 的结果缓存起来，只有当知道样式确实发生变更时才重新计算
// 2. 批量处理：如果有多个元素需要查询样式，尝试一次性收集所有元素并批量调用 getComputedStyle，而不是逐个查询，以减少 DOM 访问和计算次数
// 3. 使用事件监听：对于动态变化的样式，使用 MutationObserver 或相关的事件（如 transitionend、animationend）来监听样式变化，而不是频繁轮询
// 4. 优化查询时机：
// 4.1 避免在动画帧、滚动事件等高频率回调中无必要地调用 getComputedStyle
// 4.2 尽量在微任务中执行，利用浏览器的异步更新机制，减少对主线程的阻塞
// 5. 减少不必要的样式计算：
// 5.1 精简 CSS 规则，避免过度复杂和冗余的样式
// 5.2 使用 CSS Containment 属性（如 contain: layout）来限制样式计算的范围

// 考虑替代方案：
// 如果只需要检测特定样式属性的变化，可以尝试使用更轻量级的 API，如 elment.getBoundingClientRect()（获取布局信息）或 elment.classList.contains()（检查类名存在）等
////// elment.getBoundingClientRect() 方法相对于 window.getComputedStyle() 在某些情况下可以被认为是一种性能优化的选择，主要是基于以下几个原因：
// 1. 更有限的计算范围：
// getComputedStyle 计算的是元素的所有计算样式属性，包括但不限于尺寸、颜色、字体、边框、定位、背景等，这涉及对整个 CSSOM（CSS Object Model）和样式层叠过程的遍历。
// 而 getBoundingClientRect() 只关心元素在视口中的几何位置和尺寸（top, right, bottom, left, width, height），计算范围明显更为有限
// 它直接从布局引擎获取已计算好的布局信息，无需处理样式层叠和解析等复杂过程，因此通常具有较低的计算成本
// 2. 避免样式计算：
// getBoundingClientRect() 不涉及样式计算，它返回的是浏览器已经完成布局后，元素在当前视口坐标系统下的实际位置和大小
// 这意味着即使元素的样式发生变化，只要尚未触发布局更新，该方法的结果不会立即改变
// 相比之下，getComputedStyle 需要实时反映样式变化，每次调用都可能触发样式计算
// 3. 非阻塞性：
// getBoundingClientRect() 不触发重排（reflow）或重绘（repaint）。它提供的信息是布局阶段的副产品，浏览器在渲染过程中已经计算好并存储了这些数据
// 因此，调用此方法不会直接影响渲染流水线，避免了可能阻塞主线程的操作
// 4. 更低的内存开销：
// getComputedStyle 返回一个包含所有计算样式的 CSSStyleDeclaration 对象，如果频繁调用且不及时释放内存，可能会积累大量临时对象
// 相反，getBoundingClientRect() 返回的是一个相对轻量的 DOMRect 对象，仅包含位置和尺寸信息，内存占用较小
// 5. 适用场景明确：
// 当仅需要获取元素的布局相关数据（如位置、尺寸）时，使用 getBoundingClientRect() 直接满足需求，无需处理与当前任务无关的其他样式信息
// 针对性更强的方法使用可以避免不必要的计算和资源消耗
// 6. 更好的跨浏览器兼容性：
// getBoundingClientRect() 是一个历史悠久且广泛支持的 API，其性能表现和行为在各主流浏览器中通常更为一致，减少了因浏览器差异带来的潜在性能问题

// 对外接口
export function getStyle(
  el: AnyElement | any,
  prop?: string,
  pseudo?: string // :before
) {
  // 利用 vnode 缓存 style
  const vnode = getVNode(el);
  vnode.props.style = vnode.props.style || {};
  if (!prop) {
    // read all
    // return atomReadStyles(el);
    return vnode.props.style;
  } else {
    // read single
    // return atomReadStyle(el, prop, pseudo);
    return (
      vnode.props.style[prop] ||
      vnode.props.style[toCamel(prop)] ||
      vnode.props.style[toKebab(prop)] ||
      ""
    );
  }
}

// 对外接口
export function setStyle(
  el: HTMLElement | any,
  prop: string | Record<string, string | CanvasGradient>,
  value?: string
) {
  updateVNode(el, (vnode: VNode) => {
    vnode.props.style = vnode.props.style || {};
    if (typeof prop == "string" && value) {
      vnode.props.style[prop] = value;
    } else if (prop instanceof Object) {
      Object.keys(prop).forEach((key: string) => {
        vnode.props.style[key] = prop[key];
      });
    }
  });
  scheduleVNodeAsap();
}

export function atomReadStyles(
  el: HTMLElement | any,
  justInlineStyle = false // 是否只读内联样式
): Record<string, string> {
  const styles: Record<string, string> = {};
  if (!justInlineStyle) {
    if (getComputedStyle) {
      const s = getComputedStyle(el);
      for (let i = 0, n = s.length; i < n; i++) {
        const prop = s[i];
        styles[prop] = s.getPropertyValue(prop);
      }
    } else {
      // 兼容 内联样式 和 IE
      const s = el.style || el.currentStyle;
      for (const prop in s) {
        if (Object.prototype.hasOwnProperty.call(s, prop)) {
          styles[prop] = s[prop];
        }
      }
    }
  }
  // 内联样式
  if (el.style.cssText) {
    el.style.cssText
      .split(";")
      .filter((a: string) => !!a) // filter: non-empty
      .forEach((a: string) => {
        const [k, v] = a.split(":").map((s) => s.trim());
        if (k && v) {
          styles[k] = v;
        }
      });
  }
  return styles;
}

export function readStyleFromStyles(
  styles: Record<string, string>,
  prop: string
): string {
  const prefixedProps = getPrefixedStyleNames(prop);
  const prefixedPropsCount = prefixedProps.length;
  let value = "";
  for (let i = 0; i < prefixedPropsCount; i++) {
    const prefixedProp = prefixedProps[i];
    value = styles[prefixedProp] || "";
    if (value) {
      return value;
    }
  }
  return value;
}

export function atomReadStyle(
  el: HTMLElement | any,
  prop: string,
  pseudo?: string // :before
): string {
  const prefixedProps = getPrefixedStyleNames(prop);
  const prefixedPropsCount = prefixedProps.length;
  let value = "";
  for (let i = 0; i < prefixedPropsCount; i++) {
    const prefixedProp = prefixedProps[i];
    value =
      ("style" in el && el.style[prefixedProp]) ||
      ("currentStyle" in el && el.currentStyle[prefixedProp]) ||
      "";
    if (value) {
      return value;
    }
  }
  if (getComputedStyle) {
    const style = getComputedStyle(el, pseudo);
    for (let i = 0; i < prefixedPropsCount; i++) {
      const prefixedProp = prefixedProps[i];
      // const priority = style.getPropertyPriority(prefixedProp);
      // value =
      //   style.getPropertyValue(prefixedProp).trim() +
      //   (priority ? "!" + priority : "");
      value = style.getPropertyValue(prefixedProp).trim();
      if (value) {
        return value;
      }
    }
  }
  return value;
}

export function atomWriteStyle(
  el: HTMLElement | any,
  prop: string | Record<string, string>,
  value?: string
) {
  if (!prop) {
    return;
  }
  if (typeof prop == "string" && value) {
    try {
      el.style.setProperty(
        prop,
        value.replace(/!important/i, ""),
        value.toLowerCase().includes("!important") ? "important" : ""
      );
    } catch (e) {
      el.style[prop] = value;
    }
  } else if (prop instanceof Object) {
    Object.keys(prop).forEach((k: string) => {
      atomWriteStyle(el, k, prop[k]);
    });
  }
}

export function atomRemoveStyle(
  el: HTMLElement | any,
  prop: string | Record<string, string>
) {
  if (typeof prop == "string") {
    el.style.removeProperty(toKebab(prop));
  } else {
    Object.keys(prop).forEach((key: string) => {
      atomRemoveStyle(el, key);
    });
  }
}

// `
// :root {}
// :host {}
// `
// :root, :host {
//   --color-primary: #5A82F0;
//   --linear-gradient-blue: linear-gradient(to right, #50c8fa, var(--color-primary));
//   --linear-gradient-red: linear-gradient(to right, #ff9696, var(--color-red));
//   --linear-gradient-green: linear-gradient(to right, #50f096, var(--color-green));
// }
export function injectStyle(
  styleSheetInnerText: string,
  el?: AnyElement | string
) {
  if (!document) {
    return () => {};
  }
  let dom: Element | ShadowRoot | null;
  if (typeof el === "string") {
    const shadowRoot = document.querySelector(el)?.shadowRoot;
    if (shadowRoot) {
      dom = shadowRoot;
    } else {
      dom = document.querySelector(el);
    }
  } else {
    dom = el || document.getElementsByTagName("head")[0];
  }
  if (dom) {
    const style = document.createElement("style");
    style.innerHTML = styleSheetInnerText;
    dom.appendChild(style);
    return () => {
      if (dom instanceof HTMLElement || dom instanceof ShadowRoot) {
        dom.removeChild(style);
      }
    };
  } else {
    // The first CSS rule is : document.styleSheets[0].cssRules[0].cssText

    // const sheet = new CSSStyleSheet();
    // sheet.replaceSync(`.color { color: pink }`);
    // host.shadowRoot.adoptedStyleSheets.push(sheet);

    const position = document.styleSheets[0].insertRule(styleSheetInnerText);

    return () => {
      document.styleSheets[0].deleteRule(position);
    };
  }
}

class CSSRule {
  public selector: string;
  public name: string;
  public value: string;

  constructor(selector: string, name: string, value: string) {
    this.selector = selector;
    this.name = name;
    this.value = value;
  }

  public toString() {
    return `${this.selector} { ${this.name}: ${this.value} }`;
  }
}

class RuleList {
  public propertyName: string;
  public values: CSSRule[] = [];

  constructor(propertyName: string) {
    this.propertyName = propertyName;
  }

  public add(selector: string, value: string) {
    this.values.push(new CSSRule(selector, this.propertyName, value));
  }
}

export async function getStyleSheets() {
  const styleSheets: string[] = [];
  const styleNodes = document.querySelectorAll("style, link");
  for (let i = 0, c = styleNodes.length; i < c; i++) {
    const node = styleNodes[i];
    let sheetCssText = node.innerHTML;
    switch (node.nodeName.toLowerCase()) {
      case "style":
        // does not work with inline styles because IE doesn't allow you to get the text content of a STYLE element
        // if (
        //   sheetCssText.indexOf("-->") != -1 &&
        //   sheetCssText.indexOf("<!--") != -1
        // ) {
        //   sheetCssText.replace("<!--", "").replace("-->", "");
        // }
        break;
      case "link":
        sheetCssText = (await ajax((node as any).href, {
          method: "GET",
          responseType: "text"
        })) as string;
        break;
    }
    sheetCssText = sheetCssText
      .replace(/\/\/.+?(?=\n|\r|$)|\/\*[\s\S]+?\*\//g, "") // MultiLineComment
      .replace(/@[^{};]*;|@[^{};]*\{[^}]*\}/g, "") // AtRule
      .replace(/[\\n\n]+(?<=[\\n\n]+)\s+/g, "");
    styleSheets.push(sheetCssText);
  }
  return styleSheets;
}

export async function getRules() {
  const ruleLists: { [index: string]: RuleList } = {};
  const styleSheets = await getStyleSheets();
  for (let i = 0, n = styleSheets.length; i < n; i++) {
    const sheet = styleSheets[i];
    const rules = sheet.match(/[^{]*{[^}]*}/g);
    if (!rules) {
      continue;
    }
    for (let j = 0, c = rules.length; j < c; j++) {
      const parsedRule = rules[j].split(/[{}]/g);
      const selector = parsedRule[0].trim();
      const propertiesStr = parsedRule[1];
      const properties = propertiesStr.split(";");
      for (let k = 0; k < properties.length; k++) {
        if (properties[k].trim() != "") {
          const splitProperty = properties[k].split(":");
          const name = splitProperty[0].trim().toLowerCase();
          const value = splitProperty[1];
          if (!ruleLists[name]) {
            ruleLists[name] = new RuleList(name);
          }
          if (value) {
            ruleLists[name].add(selector, value.trim());
          }
        }
      }
    }
  }
  return ruleLists;
}
